import axios, { AxiosError } from 'axios';

export type ApiErrorMessageOptions = {
  /** Shown before the detail, e.g. "Import failed". */
  action?: string;
};

const defaultAction = 'Request failed';

const TRANSIENT_CONNECTION_DETAIL =
  /ECONNRESET|ECONNREFUSED|ETIMEDOUT|EPIPE|ENOTFOUND|socket hang up|MongoNetworkError|MongoServerSelectionError/i;

const isTransientConnectionDetail = (detail: string): boolean =>
  TRANSIENT_CONNECTION_DETAIL.test(detail);

const logHiddenApiError = (hiddenDetail: unknown, error: unknown): void => {
  console.warn('[apiErrorMessage] hiding detail from user:', hiddenDetail, error);
};

export const isNetworkOrUnreachableError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  if (error.code === 'ERR_CANCELED') {
    return false;
  }

  if (!error.response) {
    return true;
  }

  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
};

/** True when the configured API host is on this machine (Wi‑Fi off may still work). */
export const isLocalhostApiUrl = (): boolean => {
  const raw = process.env.REACT_APP_API_URL;
  if (!raw) {
    return false;
  }
  try {
    const hostname = new URL(raw).hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]'
    );
  } catch {
    return false;
  }
};

export const getApiErrorMessage = (
  error: unknown,
  options: ApiErrorMessageOptions = {},
): string => {
  const action = options.action ?? defaultAction;

  if (!axios.isAxiosError(error)) {
    logHiddenApiError(error, error);
    return `${action}. Please try again.`;
  }

  const requestUrl =
    error.config?.baseURL && error.config?.url
      ? `${error.config.baseURL}${error.config.url}`
      : (error.config?.url ?? 'the API');

  const status = error.response?.status;
  const data = error.response?.data as
    | { message?: string; error?: string }
    | string
    | undefined;

  if (typeof data === 'string' && data.trim()) {
    if (isTransientConnectionDetail(data)) {
      logHiddenApiError(data, error);
      return `Couldn't ${action} due to a temporary connection issue. Please try again.`;
    }
    return status ? `${action} (${status}): ${data}` : `${action}: ${data}`;
  }

  if (data && typeof data === 'object') {
    if (data.message) {
      if (isTransientConnectionDetail(data.message)) {
        logHiddenApiError(data.message, error);
        return `Couldn't ${action} due to a temporary connection issue. Please try again.`;
      }
      return status
        ? `${action} (${status}): ${data.message}`
        : `${action}: ${data.message}`;
    }
    if (data.error) {
      if (isTransientConnectionDetail(data.error)) {
        logHiddenApiError(data.error, error);
        return `Couldn't ${action} due to a temporary connection issue. Please try again.`;
      }
      return status
        ? `${action} (${status}): ${data.error}`
        : `${action}: ${data.error}`;
    }
  }

  if (status === 401) {
    return 'You must be logged in. Go to Login and try again.';
  }

  if (status && status >= 500) {
    logHiddenApiError(
      { status, data: error.response?.data, message: error.message },
      error,
    );
    return 'The server is temporarily unavailable. Please try again in a moment.';
  }

  if (status) {
    return `${action} (HTTP ${status}).`;
  }

  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return 'You appear to be offline. Check your internet connection.';
    }
    return `Cannot reach the server (${requestUrl}). Check that the API is running and your connection is working.`;
  }

  if (error.code === 'ECONNABORTED') {
    return 'The request timed out. The server may be unreachable.';
  }

  return `${action}: ${error.message}`;
};

export const getGlobalApiToastMessage = (error: AxiosError): string =>
  getApiErrorMessage(error, { action: 'Cannot reach the server' });
